import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Box, Check, FileText, Loader2, Sparkles, X } from "lucide-react"
import { Dialog } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { consumeSSE } from "@/lib/sse"
import { cn } from "@/lib/cn"

type AgentOption = { id: number; name: string; key: string }

type Props = {
  open: boolean
  onClose: () => void
  agents: AgentOption[]
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")

const BEST_PRACTICES = [
  "State the trigger in one sentence (what the user just said).",
  "Describe the process step-by-step, naming the platform tools to call.",
  "Mention any data the skill must collect before its terminal tool fires.",
]

// Triggers the green-tip nudge: any mention of partner-data-shaped
// concepts in the operator's description means a source_key is going
// to materially improve the draft. Liberal — false positives just
// re-emphasize an already-correct hint; false negatives leave the
// operator without it.
const PARTNER_DATA_HINT = /\b(endpoint|end[\s-]point|source[_\s-]?key|sourcekey|partner\s*(?:api|data|call|fetch|integration)?|fetch_partner_data|webhook|route|integration|api\s*(?:call|integration|endpoint|response)?|call\s+the\b|hit\s+the\b)\b/i

// Phase keys + labels — must match Portal::Skills::CreateStream's emits.
// Order is the order rendered in the progress checklist.
const PHASES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "validate",           label: "Validating inputs" },
  { key: "drafting_skill",     label: "Drafting skill prompt with Forge" },
  { key: "persisting_skill",   label: "Saving draft" },
  { key: "attaching_agents",   label: "Attaching to agents" },
  { key: "drafting_scenarios", label: "Drafting red-team scenarios" },
]

type PhaseStatus = "pending" | "running" | "completed" | "skipped" | "error"
type PhaseState  = Record<string, { status: PhaseStatus; detail?: string; chars?: number; tail?: string }>

const initialPhaseState = (): PhaseState =>
  PHASES.reduce((acc, p) => ({ ...acc, [p.key]: { status: "pending" } }), {})

// Anthropic ~3.5 chars/token for English — close enough for a counter.
const approxTokens = (chars: number) => Math.round(chars / 3.5)

export function NewSkillModal({ open, onClose, agents }: Props) {
  const [name, setName]               = useState("")
  const [slug, setSlug]               = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [agentIds, setAgentIds]       = useState<number[]>(agents[0] ? [agents[0].id] : [])
  const [mode, setMode]               = useState<"manual" | "ai">("manual")
  const [description, setDescription] = useState("")
  const [submitting, setSubmitting]   = useState(false)

  // Streaming state — only used by AI mode.
  const [phaseState, setPhaseState]   = useState<PhaseState>(initialPhaseState)
  const [streamError, setStreamError] = useState<string | null>(null)

  const submitDisabled = useMemo(() => {
    if (submitting) return true
    if (!name.trim() || !slug.trim()) return true
    if (mode === "ai" && description.trim().length < 10) return true
    return false
  }, [submitting, name, slug, mode, description])

  function onNameChange(v: string) {
    setName(v)
    if (!slugTouched) setSlug(slugify(v))
  }

  function onSlugChange(v: string) {
    setSlug(slugify(v))
    setSlugTouched(true)
  }

  function reset() {
    setName("")
    setSlug("")
    setSlugTouched(false)
    setAgentIds(agents[0] ? [agents[0].id] : [])
    setMode("manual")
    setDescription("")
    setSubmitting(false)
    setPhaseState(initialPhaseState())
    setStreamError(null)
  }

  function toggleAgent(id: number) {
    setAgentIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
  }

  function close() {
    if (submitting) return
    reset()
    onClose()
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitDisabled) return
    if (mode === "ai") return submitAi()
    submitManual()
  }

  function submitManual() {
    setSubmitting(true)
    router.post(
      "/skills",
      {
        skill: { name, key: slug, agent_ids: agentIds },
        mode,
        description: "",
      },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: () => {
          reset()
          onClose()
        },
      },
    )
  }

  // AI mode: open the SSE-streamed create endpoint and render phase
  // progress in real time. On `done`, navigate to the redirect URL the
  // server emits (Test Lab > Design tab on the new skill).
  async function submitAi() {
    setSubmitting(true)
    setStreamError(null)
    setPhaseState(initialPhaseState())

    let redirectUrl: string | null = null

    await consumeSSE(
      "/skills/stream_create",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          skill:       { name, key: slug, agent_ids: agentIds },
          description: description,
        }),
      },
      {
        onEvent: (event, raw) => {
          const data = (raw ?? {}) as Record<string, unknown>
          switch (event) {
            case "phase": {
              const key    = data.phase as string
              const status = (data.status as string) || "running"
              setPhaseState((prev) => {
                const next = { ...prev }
                next[key] = { status: status as PhaseStatus, detail: (data.message as string) || (data.scenario_count != null ? `${data.scenario_count} scenarios` : undefined), chars: prev[key]?.chars, tail: prev[key]?.tail }
                if (status === "started") next[key] = { status: "running", detail: (data.message as string) || undefined }
                if (status === "completed" || status === "skipped") {
                  next[key] = { status: status as PhaseStatus, detail: next[key].detail, chars: prev[key]?.chars, tail: prev[key]?.tail }
                }
                return next
              })
              break
            }
            case "token_delta": {
              // Throttled to ~5 Hz on the server, so this is safe to set
              // straight into state without further FE throttling.
              const key   = data.phase as string
              const chars = (data.chars as number) || 0
              const tail  = (data.tail  as string) || ""
              setPhaseState((prev) => ({
                ...prev,
                [key]: { ...(prev[key] || { status: "running" as PhaseStatus }), chars, tail },
              }))
              break
            }
            case "done": {
              redirectUrl = (data.redirect as string) || null
              break
            }
            case "error": {
              const msg = (data.message as string) || "Skill creation failed."
              setStreamError(msg)
              // Mark the currently-running phase as errored.
              setPhaseState((prev) => {
                const next = { ...prev }
                const running = PHASES.find((p) => prev[p.key]?.status === "running")
                if (running) next[running.key] = { status: "error", detail: msg }
                return next
              })
              break
            }
          }
        },
        onError: (msg) => setStreamError(msg),
      },
    )

    setSubmitting(false)
    if (redirectUrl) {
      reset()
      onClose()
      router.visit(redirectUrl)
    }
  }

  // While AI mode is streaming, swap the form for the progress view.
  const showProgress = submitting && mode === "ai"

  return (
    <Dialog open={open} onClose={close} size="lg">
      <form onSubmit={submit}>
        {/* ───── Header ───── */}
        <header className="flex items-start gap-4 border-b border-border px-7 py-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-control border border-brand-red/30 bg-brand-redSoft text-brand-red">
            <Box size={20} strokeWidth={1.7} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold">{showProgress ? "Drafting skill…" : "New skill"}</h2>
            <p className="mt-0.5 text-sm text-app-muted">
              {showProgress
                ? "Forge is generating the skill prompt and red-team scenarios. This usually takes 10–30 seconds."
                : "Drafts start disabled. Promote to live when ready."}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="-mr-2 -mt-1 rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        {showProgress ? (
          <ProgressView phaseState={phaseState} streamError={streamError} />
        ) : (
          <>
            {/* ───── Body: two columns ───── */}
            <div className="grid gap-7 px-7 py-6 md:grid-cols-2">
              {/* Left column — identity */}
              <div className="space-y-5">
                <Section
                  title="Name"
                  hint="Shown in the skills catalog and Test Lab."
                >
                  <Input
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Get user coin balance"
                    required
                    autoFocus
                  />
                </Section>

                <Section
                  title="Slug"
                  hint="snake_case key, unique per agent. Auto-derived from Name; editable."
                >
                  <Input
                    value={slug}
                    onChange={(e) => onSlugChange(e.target.value)}
                    placeholder="get_user_coin_balance"
                    required
                    className="font-mono"
                  />
                </Section>

                <Section
                  title="Assign to agents"
                  hint="A skill can live on any number of agents — pick zero or more. You can change this later on the Edit page."
                >
                  {agents.length === 0 ? (
                    <div className="rounded-control border border-border bg-white/5 px-3 py-2 text-sm text-app-muted">
                      No agents in this company yet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {agents.map((a) => {
                        const checked = agentIds.includes(a.id)
                        return (
                          <label
                            key={a.id}
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-control border px-3 py-2 text-sm transition",
                              checked
                                ? "border-brand-red/40 bg-brand-redSoft"
                                : "border-border bg-white/[0.03] hover:bg-white/[0.06]",
                            )}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-brand-red"
                              checked={checked}
                              onChange={() => toggleAgent(a.id)}
                            />
                            <span className="flex-1 text-app-text">{a.name}</span>
                            <code className="text-xs text-app-muted">{a.key}</code>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </Section>
              </div>

              {/* Right column — creation mode */}
              <div className="space-y-5">
                <Section
                  title="Creation mode"
                  hint="Choose how you'd like to create this skill."
                >
                  <div className="space-y-3">
                    <ModeCard
                      selected={mode === "manual"}
                      onSelect={() => setMode("manual")}
                      icon={<FileText size={22} strokeWidth={1.7} />}
                      title="Manual"
                      description="Create an empty draft, edit fields directly."
                    />
                    <ModeCard
                      selected={mode === "ai"}
                      onSelect={() => setMode("ai")}
                      icon={<Sparkles size={22} strokeWidth={1.7} />}
                      title="AI (Forge)"
                      description="Drafts skill prompt + tools + a red-team test suite from your description."
                    />
                  </div>
                </Section>

                {mode === "ai" && (
                  <Section
                    title="Describe the skill"
                    hint="One paragraph. Best practices below."
                  >
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={6}
                      placeholder="When a user asks about their coin balance, fetch it from the partner endpoint coin_balance and report it as 'You have N coins (≈ $X)'. On error, apologize and offer to escalate."
                    />
                    <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-app-muted">
                      {BEST_PRACTICES.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                    {(() => {
                      const partnerHinted = PARTNER_DATA_HINT.test(description)
                      return (
                        <div className={cn(
                          "mt-3 rounded-control border px-3 py-2 text-xs transition-colors",
                          partnerHinted
                            ? "border-status-success/40 bg-status-success/10 text-status-success"
                            : "border-border bg-white/[0.03] text-app-muted",
                        )}>
                          <span className={cn("font-medium", partnerHinted ? "text-status-success" : "text-app-text")}>
                            {partnerHinted ? "Tip — looks like this skill calls a partner. " : "Tip — "}
                          </span>
                          Supply a {" "}
                          <code className={cn("rounded px-1 font-mono", partnerHinted ? "bg-status-success/15" : "bg-white/5")}>source_key</code>{" "}
                          to pull live sample data. Make sure the user behind that
                          key is in the <strong>positive state</strong> for this
                          skill (has installed offers, a non-zero balance, unclaimed
                          rewards — whatever applies). Otherwise every partner
                          endpoint comes back empty and the staged test scenarios
                          won't reflect realistic shapes.
                        </div>
                      )
                    })()}
                  </Section>
                )}
              </div>
            </div>
          </>
        )}

        {/* ───── Footer ───── */}
        <footer className="flex justify-end gap-3 border-t border-border px-7 py-4">
          <Button type="button" variant="ghost" onClick={close} disabled={submitting}>
            {showProgress ? "Cancel" : "Cancel"}
          </Button>
          {!showProgress && (
            <Button type="submit" disabled={submitDisabled}>
              {submitting
                ? mode === "ai" ? "Drafting with Forge…" : "Creating…"
                : "Create skill"}
            </Button>
          )}
        </footer>
      </form>
    </Dialog>
  )
}

// ─── Progress checklist ──────────────────────────────────────────────
function ProgressView({
  phaseState, streamError,
}: {
  phaseState: PhaseState
  streamError: string | null
}) {
  return (
    <div className="space-y-3 px-7 py-6">
      <ol className="space-y-2.5">
        {PHASES.map((p) => {
          const s = phaseState[p.key] ?? { status: "pending" as PhaseStatus }
          return (
            <PhaseRow
              key={p.key}
              label={p.label}
              status={s.status}
              detail={s.detail}
              chars={s.chars}
              tail={s.tail}
            />
          )
        })}
      </ol>
      {streamError && (
        <div className="mt-3 rounded-control border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
          {streamError}
        </div>
      )}
    </div>
  )
}

// Cycles ".", "..", "...", "...." every ~350ms. Width-locked via 4-wide
// padding so the row doesn't reflow on each tick.
function ThinkingDots() {
  const [n, setN] = useState(1)
  useEffect(() => {
    const id = setInterval(() => setN((c) => (c % 4) + 1), 350)
    return () => clearInterval(id)
  }, [])
  return <span aria-hidden>{".".repeat(n).padEnd(4, "\u00A0")}</span>
}

function PhaseRow({
  label, status, detail, chars, tail,
}: {
  label: string
  status: PhaseStatus
  detail?: string
  chars?: number
  tail?: string
}) {
  const icon =
    status === "running"   ? <Loader2 size={14} className="animate-spin text-brand-red" /> :
    status === "completed" ? <Check size={14} className="text-status-success" /> :
    status === "skipped"   ? <Check size={14} className="text-app-subtle" /> :
    status === "error"     ? <X size={14} className="text-status-error" /> :
                             <span className="block h-3.5 w-3.5 rounded-full border border-border" />

  const textTone =
    status === "running"   ? "text-app-text" :
    status === "completed" ? "text-app-text" :
    status === "skipped"   ? "text-app-subtle line-through" :
    status === "error"     ? "text-status-error" :
                             "text-app-subtle"

  // Token counter + tail snippet — only meaningful once Forge has
  // actually started writing. Anthropic's TTFT (time-to-first-token)
  // on a fresh request can be 3-15s for a large prompt; before the
  // first token_delta arrives, "0 tokens" makes the UI look broken.
  // "Waiting for first token…" reads as expected behavior.
  const isWaitingFirstToken = status === "running" && (chars === undefined || chars === 0)
  const showStream          = status === "running" && chars !== undefined && chars > 0
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className={cn("text-sm", textTone)}>{label}</div>
          {showStream && (
            <span className="shrink-0 font-mono text-[11px] text-app-muted tabular-nums">
              ~{approxTokens(chars!).toLocaleString()} tokens
            </span>
          )}
          {isWaitingFirstToken && (
            <span className="shrink-0 font-mono text-[11px] italic text-app-subtle tabular-nums">
              thinking<ThinkingDots />
            </span>
          )}
        </div>
        {detail && <div className="mt-0.5 text-xs text-app-muted">{detail}</div>}
        {showStream && tail && (
          <div className="mt-1 truncate font-mono text-[11px] text-app-subtle/80">
            …{tail}
          </div>
        )}
      </div>
    </li>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-base font-semibold text-app-text">{title}</div>
      {hint && <div className="mb-2 mt-0.5 text-xs text-app-muted">{hint}</div>}
      {children}
    </div>
  )
}

function ModeCard({
  selected,
  onSelect,
  icon,
  title,
  description,
}: {
  selected: boolean
  onSelect: () => void
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-4 rounded-control border px-4 py-4 text-left transition",
        selected
          ? "border-brand-red bg-brand-redSoft shadow-redGlow"
          : "border-border bg-white/[0.03] hover:border-border-strong hover:bg-white/[0.06]",
      )}
    >
      <RadioDot selected={selected} />
      <span className="flex flex-1 items-start gap-3">
        <span className={cn("mt-0.5 shrink-0", selected ? "text-brand-red" : "text-app-muted")}>
          {icon}
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-app-text">{title}</span>
          <span className="mt-0.5 block text-sm text-app-muted">{description}</span>
        </span>
      </span>
    </button>
  )
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition",
        selected ? "border-brand-red bg-brand-red/10" : "border-border-strong bg-transparent",
      )}
      aria-hidden
    >
      {selected && <span className="h-2 w-2 rounded-full bg-brand-red" />}
    </span>
  )
}
